import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Building2,
  Mail,
  Phone,
  Clock,
  CheckCircle2,
  XCircle,
  X,
  Save,
  AlertCircle,
  ListChecks,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Company, ViolationType } from '../types';
import apiService from '../services/api';
import { formatDateTime } from '../utils/timeUtils';

const CompaniesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [companies, setCompanies] = useState<Company[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    companyName: '',
    commercialRegistration: '',
    email: '',
    phone: '+974',
    address: '',
    slaHours: 24,
    ownerPassword: '',
  });

  // ── Violation types state ──────────────────────────────────────────────────
  const [vtDialogOpen, setVtDialogOpen] = useState(false);
  const [vtCompany, setVtCompany] = useState<Company | null>(null);
  const [violationTypes, setViolationTypes] = useState<ViolationType[]>([]);
  const [vtLoading, setVtLoading] = useState(false);
  const [vtError, setVtError] = useState('');
  const [vtFormData, setVtFormData] = useState({ name: '', price: '' });
  const [editingVt, setEditingVt] = useState<ViolationType | null>(null);
  const [vtSaving, setVtSaving] = useState(false);
  // ──────────────────────────────────────────────────────────────────────────

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getCompanies();
      setCompanies(data);
      setFilteredCompanies(data);
      setError('');
    } catch (err) {
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    const filtered = companies.filter((company) =>
      company.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.commercialRegistration.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCompanies(filtered);
    setCurrentPage(1);
  }, [searchTerm, companies]);

  const handleOpenDialog = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        companyName: company.companyName,
        commercialRegistration: company.commercialRegistration,
        email: company.email,
        phone: company.phone,
        address: company.address || '',
        slaHours: company.slaHours,
        ownerPassword: '',
      });
    } else {
      setEditingCompany(null);
      setFormData({
        companyName: '',
        commercialRegistration: '',
        email: '',
        phone: '+974',
        address: '',
        slaHours: 24,
        ownerPassword: '',
      });
    }
    setDialogOpen(true);
    setFieldErrors({});
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCompany(null);
    setFieldErrors({});
    setError('');
  };

  const handleSave = async () => {
    try {
      setFieldErrors({});
      setError('');

      if (editingCompany) {
        await apiService.updateCompany(editingCompany.id, formData);
      } else {
        const dataToSend = {
          ...formData,
          ownerFirstName: formData.companyName.split(' ')[0] || 'Owner',
          ownerLastName: formData.companyName.split(' ').slice(1).join(' ') || 'Account',
          ownerEmail: formData.email,
          ownerPhone: formData.phone,
        };
        await apiService.createCompany(dataToSend);
      }
      await fetchCompanies();
      handleCloseDialog();
    } catch (err: any) {
      if (err.response?.data) {
        const responseData = err.response.data;

        if (responseData.message && Array.isArray(responseData.message)) {
          const errorMessage = responseData.message[0] || 'Failed to save company';
          setError(errorMessage);
        } else {
          const fieldMapping: Record<string, string> = {
            'company_name': 'companyName',
            'commercial_registration': 'commercialRegistration',
            'email': 'email',
            'phone': 'phone',
            'address': 'address',
            'custom_sla_hours': 'slaHours',
            'ownerPassword': 'ownerPassword',
          };

          const errors: Record<string, string> = {};
          Object.keys(responseData).forEach((key) => {
            const frontendKey = fieldMapping[key] || key;
            const errorValue = responseData[key];
            if (Array.isArray(errorValue)) {
              errors[frontendKey] = errorValue[0];
            } else if (typeof errorValue === 'string') {
              errors[frontendKey] = errorValue;
            }
          });

          setFieldErrors(errors);
          setError('Please fix the errors below');
        }
      } else {
        setError('Failed to save company');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;

    try {
      await apiService.deleteCompany(id);
      await fetchCompanies();
    } catch (err) {
      setError('Failed to delete company');
    }
  };

  // ── Violation types handlers ──────────────────────────────────────────────
  const openVtDialog = async (company: Company) => {
    setVtCompany(company);
    setVtError('');
    setVtFormData({ name: '', price: '' });
    setEditingVt(null);
    setVtDialogOpen(true);
    setVtLoading(true);
    try {
      const data = await apiService.getViolationTypes(String(company.id));
      setViolationTypes(data);
    } catch {
      setVtError(isRTL ? 'فشل تحميل أنواع الخطية' : 'Failed to load violation types');
    } finally {
      setVtLoading(false);
    }
  };

  const closeVtDialog = () => {
    setVtDialogOpen(false);
    setVtCompany(null);
    setViolationTypes([]);
    setEditingVt(null);
    setVtFormData({ name: '', price: '' });
    setVtError('');
  };

  const handleVtStartEdit = (vt: ViolationType) => {
    setEditingVt(vt);
    setVtFormData({ name: vt.name, price: String(vt.price) });
    setVtError('');
  };

  const handleVtCancelEdit = () => {
    setEditingVt(null);
    setVtFormData({ name: '', price: '' });
    setVtError('');
  };

  const handleVtSave = async () => {
    if (!vtCompany) return;
    if (!vtFormData.name.trim()) {
      setVtError(isRTL ? 'اسم الخطية مطلوب' : 'Violation name is required');
      return;
    }
    const price = parseFloat(vtFormData.price);
    if (isNaN(price) || price < 0) {
      setVtError(isRTL ? 'السعر غير صالح' : 'Invalid price');
      return;
    }
    setVtSaving(true);
    try {
      if (editingVt) {
        const updated = await apiService.updateViolationType(editingVt.id, {
          name: vtFormData.name.trim(),
          price,
        });
        setViolationTypes((prev) => prev.map((v) => (v.id === editingVt.id ? { ...v, ...updated, id: updated._id || updated.id } : v)));
      } else {
        const created = await apiService.createViolationType({
          company: String(vtCompany.id),
          name: vtFormData.name.trim(),
          price,
        });
        setViolationTypes((prev) => [...prev, { ...created, id: created._id || created.id }]);
      }
      setEditingVt(null);
      setVtFormData({ name: '', price: '' });
      setVtError('');
    } catch (err: any) {
      const msg = err.response?.data?.message;
      setVtError(Array.isArray(msg) ? msg[0] : (msg || (isRTL ? 'حدث خطأ' : 'An error occurred')));
    } finally {
      setVtSaving(false);
    }
  };

  const handleVtDelete = async (vt: ViolationType) => {
    if (!window.confirm(isRTL ? `هل أنت متأكد من حذف "${vt.name}"؟` : `Delete "${vt.name}"?`)) return;
    try {
      await apiService.deleteViolationType(vt.id);
      setViolationTypes((prev) => prev.filter((v) => v.id !== vt.id));
    } catch {
      setVtError(isRTL ? 'فشل الحذف' : 'Failed to delete');
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  // Pagination
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCompanies = filteredCompanies.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-green-50 to-emerald-50 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Modern Header */}
        <div className="relative overflow-hidden rounded-3xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>

          <div className="relative px-6 py-8 md:px-10 md:py-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-white/30 rounded-2xl blur-xl"></div>
                  <div className="relative bg-white/20 backdrop-blur-md p-4 rounded-2xl border border-white/30 shadow-2xl">
                    <Building2 className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
                    {isRTL ? 'إدارة الشركات' : 'Companies Management'}
                  </h1>
                  <p className="text-white/80 text-sm md:text-base font-medium mt-1">
                    {isRTL ? 'إدارة وتتبع جميع الشركات المسجلة' : 'Manage and track all registered companies'}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={fetchCompanies}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-3 bg-white/20 backdrop-blur-md text-white rounded-xl
                           hover:bg-white/30 transition-all border border-white/30 font-semibold disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">{isRTL ? 'تحديث' : 'Refresh'}</span>
                </button>
                <button
                  onClick={() => handleOpenDialog()}
                  className="flex items-center gap-2 px-6 py-3 bg-white text-brand-700 rounded-xl
                           hover:bg-white/90 transition-all shadow-lg font-bold"
                >
                  <Plus className="w-5 h-5" />
                  <span>{isRTL ? 'إضافة شركة' : 'ADD COMPANY'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-gradient-to-r from-red-50 to-rose-50 border-r-4 border-red-500 rounded-2xl p-5 shadow-lg animate-shake">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 bg-red-100 p-2 rounded-xl">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h4 className="text-red-900 font-bold text-lg mb-1">{isRTL ? 'حدث خطأ' : 'Error'}</h4>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError('')}
                className="flex-shrink-0 text-red-400 hover:text-red-600 hover:bg-red-100 p-2 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Search & Filters Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              <Search className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={isRTL ? 'البحث عن الشركات...' : 'Search companies...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-xl
                       focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all
                       text-gray-900 placeholder-gray-400 font-medium"
            />
          </div>
        </div>

        {/* Companies Grid/Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin"></div>
              <p className="text-gray-600 font-medium">{isRTL ? 'جاري التحميل...' : 'Loading...'}</p>
            </div>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {isRTL ? 'لا توجد شركات' : 'No Companies Found'}
            </h3>
            <p className="text-gray-600">
              {searchTerm
                ? isRTL ? 'لم يتم العثور على نتائج للبحث' : 'No results found for your search'
                : isRTL ? 'ابدأ بإضافة شركة جديدة' : 'Get started by adding a new company'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Companies Table */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700">
                      <th className="px-6 py-5 text-left text-sm font-black text-white uppercase tracking-wide min-w-[200px]">
                        {isRTL ? 'اسم الشركة' : 'Company Name'}
                      </th>
                      <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide min-w-[160px] hidden md:table-cell">
                        {isRTL ? 'السجل التجاري' : 'Commercial Reg.'}
                      </th>
                      <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide min-w-[200px] hidden lg:table-cell">
                        {isRTL ? 'البريد الإلكتروني' : 'Email'}
                      </th>
                      <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide min-w-[150px] hidden sm:table-cell">
                        {isRTL ? 'الهاتف' : 'Phone'}
                      </th>
                      <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide min-w-[160px]">
                        {isRTL ? 'موعد الإصلاح (ساعات)' : 'Repair Deadline (hrs)'}
                      </th>
                      <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide min-w-[120px]">
                        {isRTL ? 'الحالة' : 'Status'}
                      </th>
                      <th className="px-6 py-5 text-center text-sm font-black text-white uppercase tracking-wide min-w-[220px]">
                        {isRTL ? 'الإجراءات' : 'Actions'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedCompanies.map((company) => (
                      <tr key={company.id} className="hover:bg-brand-50/30 transition-all group">
                        <td className="px-6 py-5 min-w-[200px]">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="font-bold text-gray-900 group-hover:text-brand-700 transition-colors">
                                {company.companyName}
                              </div>
                              <div className="text-sm text-gray-500 md:hidden">{company.commercialRegistration}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center hidden md:table-cell min-w-[160px]">
                          <span className="text-gray-700 font-semibold">{company.commercialRegistration}</span>
                        </td>
                        <td className="px-6 py-5 text-center hidden lg:table-cell min-w-[200px]">
                          <div className="flex items-center justify-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-700">{company.email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center hidden sm:table-cell min-w-[150px]">
                          <div className="flex items-center justify-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-700 font-mono">{company.phone}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center min-w-[160px]">
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-100 text-brand-800 rounded-full text-sm font-bold border border-brand-200">
                            <Clock className="w-4 h-4" />
                            {isRTL ? `${company.slaHours} ساعة` : `${company.slaHours}h`}
                          </span>
                        </td>
                        <td className="px-6 py-5 text-center min-w-[120px]">
                          {company.isActive ? (
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-sm font-bold border border-emerald-200">
                              <CheckCircle2 className="w-4 h-4" />
                              {isRTL ? 'نشط' : 'Active'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-800 rounded-full text-sm font-bold border border-gray-200">
                              <XCircle className="w-4 h-4" />
                              {isRTL ? 'غير نشط' : 'Inactive'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-5 min-w-[220px]">
                          <div className="flex items-center justify-center gap-2">
                            <div className="group">
                              <button
                                onClick={() => openVtDialog(company)}
                                className="flex items-center gap-1.5 px-3 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-all font-semibold text-xs"
                              >
                                <ListChecks className="w-4 h-4 flex-shrink-0" />
                                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
                                  {isRTL ? 'أنواع الخطية' : 'Violations'}
                                </span>
                              </button>
                            </div>
                            <div className="group">
                              <button
                                onClick={() => handleOpenDialog(company)}
                                className="flex items-center gap-1.5 px-3 py-2 bg-brand-100 text-brand-700 rounded-lg hover:bg-brand-200 transition-all font-semibold text-xs"
                              >
                                <Edit2 className="w-4 h-4 flex-shrink-0" />
                                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
                                  {isRTL ? 'تعديل' : 'Edit'}
                                </span>
                              </button>
                            </div>
                            <div className="group">
                              <button
                                onClick={() => handleDelete(company.id)}
                                className="flex items-center gap-1.5 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all font-semibold text-xs"
                              >
                                <Trash2 className="w-4 h-4 flex-shrink-0" />
                                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
                                  {isRTL ? 'حذف' : 'Delete'}
                                </span>
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="text-sm text-gray-600">
                  {isRTL ? `عرض` : 'Showing'} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredCompanies.length)} {isRTL ? 'من' : 'of'} {filteredCompanies.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold"
                  >
                    {isRTL ? 'السابق' : 'Previous'}
                  </button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = i + 1 + Math.max(0, currentPage - 3);
                      if (page > totalPages) return null;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`w-10 h-10 rounded-lg font-bold transition-all ${
                            currentPage === page
                              ? 'bg-gradient-to-br from-brand-600 to-brand-700 text-white shadow-lg'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-semibold"
                  >
                    {isRTL ? 'التالي' : 'Next'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Company Dialog */}
        {dialogOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
              {/* Dialog Header */}
              <div className="sticky top-0 bg-gradient-to-r from-brand-600 to-brand-700 px-8 py-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">
                    {editingCompany
                      ? isRTL ? 'تعديل الشركة' : 'Edit Company'
                      : isRTL ? 'إضافة شركة جديدة' : 'Add New Company'}
                  </h2>
                  <button
                    onClick={handleCloseDialog}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>

              {/* Dialog Body */}
              <div className="p-8 space-y-6">
                {/* Error Alert */}
                {error && (
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 flex items-start gap-3 animate-fadeIn">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-red-800 font-medium">{error}</p>
                    </div>
                    <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Company Name */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {isRTL ? 'اسم الشركة' : 'Company Name'} *
                    </label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 rounded-xl transition-all
                        ${fieldErrors.companyName ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:border-brand-500 focus:ring-brand-100'}
                        focus:ring-4 outline-none font-medium`}
                      placeholder={isRTL ? 'أدخل اسم الشركة' : 'Enter company name'}
                    />
                    {fieldErrors.companyName && (
                      <p className="mt-2 text-sm text-red-600 font-medium">{fieldErrors.companyName}</p>
                    )}
                  </div>

                  {/* Commercial Registration */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {isRTL ? 'رقم السجل التجاري' : 'Commercial Registration'} *
                      {editingCompany && (
                        <span className="text-xs text-gray-500 font-normal ml-2">
                          {isRTL ? '(غير قابل للتعديل)' : '(Cannot be changed)'}
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={formData.commercialRegistration}
                      onChange={(e) => setFormData({ ...formData, commercialRegistration: e.target.value })}
                      disabled={!!editingCompany}
                      className={`w-full px-4 py-3 border-2 rounded-xl transition-all
                        ${!!editingCompany ? 'bg-gray-100 cursor-not-allowed text-gray-600' : 'bg-gray-50'}
                        ${fieldErrors.commercialRegistration ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:border-brand-500 focus:ring-brand-100'}
                        focus:ring-4 outline-none font-medium`}
                      placeholder={isRTL ? 'رقم السجل التجاري' : 'CR Number'}
                    />
                    {fieldErrors.commercialRegistration && (
                      <p className="mt-2 text-sm text-red-600 font-medium">{fieldErrors.commercialRegistration}</p>
                    )}
                  </div>

                  {/* SLA Hours */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {isRTL ? 'ساعات موعد الإصلاح' : 'Repair Deadline Hours'} *
                    </label>
                    <input
                      type="number"
                      value={formData.slaHours}
                      onChange={(e) => setFormData({ ...formData, slaHours: parseInt(e.target.value) || 24 })}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl
                               focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none font-medium"
                      min="1"
                    />
                  </div>

                  {/* Email */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {isRTL ? 'البريد الإلكتروني' : 'Email'} *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 rounded-xl transition-all
                        ${fieldErrors.email ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:border-brand-500 focus:ring-brand-100'}
                        focus:ring-4 outline-none font-medium`}
                      placeholder={isRTL ? 'example@company.com' : 'example@company.com'}
                    />
                    {fieldErrors.email && (
                      <p className="mt-2 text-sm text-red-600 font-medium">{fieldErrors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {isRTL ? 'رقم الهاتف' : 'Phone Number'} *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value.startsWith('+974')) {
                          setFormData({ ...formData, phone: '+974' });
                        } else {
                          setFormData({ ...formData, phone: value });
                        }
                      }}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 rounded-xl transition-all
                        ${fieldErrors.phone ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:border-brand-500 focus:ring-brand-100'}
                        focus:ring-4 outline-none font-medium`}
                      placeholder="+97412345678"
                      maxLength={15}
                    />
                    {fieldErrors.phone && (
                      <p className="mt-2 text-sm text-red-600 font-medium">{fieldErrors.phone}</p>
                    )}
                  </div>

                  {/* Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {isRTL ? 'العنوان' : 'Address'}
                    </label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl
                               focus:border-brand-500 focus:ring-4 focus:ring-brand-100 transition-all outline-none font-medium resize-none"
                      placeholder={isRTL ? 'أدخل العنوان' : 'Enter address'}
                    />
                  </div>

                  {/* Owner Password */}
                  <div className="md:col-span-2">
                    {!editingCompany && (
                      <div className="bg-brand-50 border-2 border-brand-200 rounded-xl p-4 mb-4">
                        <p className="text-sm text-brand-800 font-medium">
                          {isRTL
                            ? 'سيتم إنشاء حساب المالك تلقائياً باستخدام بيانات الشركة'
                            : 'Owner account will be created automatically using company information'}
                        </p>
                      </div>
                    )}
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {isRTL ? 'كلمة مرور المالك' : 'Owner Password'}
                      {!editingCompany && <span className="text-red-500 ml-1">*</span>}
                      {editingCompany && (
                        <span className="text-xs text-gray-500 font-normal ml-2">
                          {isRTL ? '(اتركها فارغة للإبقاء على كلمة المرور الحالية)' : '(Leave empty to keep current password)'}
                        </span>
                      )}
                    </label>
                    <input
                      type="password"
                      value={formData.ownerPassword}
                      onChange={(e) => setFormData({ ...formData, ownerPassword: e.target.value })}
                      className={`w-full px-4 py-3 bg-gray-50 border-2 rounded-xl transition-all
                        ${fieldErrors.ownerPassword ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:border-brand-500 focus:ring-brand-100'}
                        focus:ring-4 outline-none font-medium`}
                      placeholder={isRTL ? '8 أحرف على الأقل' : 'At least 8 characters'}
                    />
                    {fieldErrors.ownerPassword && (
                      <p className="mt-2 text-sm text-red-600 font-medium">{fieldErrors.ownerPassword}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Dialog Footer */}
              <div className="sticky bottom-0 bg-gray-50 px-8 py-6 rounded-b-3xl border-t border-gray-200 flex gap-4">
                <button
                  onClick={handleCloseDialog}
                  className="flex-1 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl
                           hover:bg-gray-50 transition-all font-bold"
                >
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 text-white rounded-xl
                           hover:from-brand-700 hover:to-brand-800 transition-all shadow-lg font-bold
                           flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {isRTL ? 'حفظ' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Violation Types Dialog ────────────────────────────────────────── */}
        {vtDialogOpen && vtCompany && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col animate-scaleIn">
              {/* Header */}
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-8 py-6 rounded-t-3xl flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-white" dir={isRTL ? 'rtl' : 'ltr'}>
                      {isRTL ? 'أنواع الخطية' : 'Violation Types'}
                    </h2>
                    <p className="text-amber-100 text-sm mt-0.5 font-medium">{vtCompany.companyName}</p>
                  </div>
                  <button
                    onClick={closeVtDialog}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {/* Error */}
                {vtError && (
                  <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-3 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-sm font-medium flex-1">{vtError}</p>
                    <button onClick={() => setVtError('')} className="text-red-400 hover:text-red-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Add / Edit form */}
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 space-y-4">
                  <h3 className="font-bold text-amber-800 text-sm" dir={isRTL ? 'rtl' : 'ltr'}>
                    {editingVt
                      ? isRTL ? 'تعديل نوع الخطية' : 'Edit Violation Type'
                      : isRTL ? 'إضافة نوع خطية جديد' : 'Add New Violation Type'}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-600 mb-1" dir={isRTL ? 'rtl' : 'ltr'}>
                        {isRTL ? 'اسم الخطية' : 'Violation Name'} *
                      </label>
                      <input
                        type="text"
                        value={vtFormData.name}
                        onChange={(e) => setVtFormData({ ...vtFormData, name: e.target.value })}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        className="w-full px-3 py-2.5 bg-white border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm font-medium"
                        placeholder={isRTL ? 'مثال: تأخر في إنجاز المهمة' : 'e.g. Late task completion'}
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-600 mb-1" dir={isRTL ? 'rtl' : 'ltr'}>
                        {isRTL ? 'السعر (ريال)' : 'Price (QAR)'} *
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={vtFormData.price}
                        onChange={(e) => setVtFormData({ ...vtFormData, price: e.target.value })}
                        dir="ltr"
                        className="w-full px-3 py-2.5 bg-white border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm font-medium"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    {editingVt && (
                      <button
                        onClick={handleVtCancelEdit}
                        className="flex-1 px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all text-sm font-bold"
                      >
                        {isRTL ? 'إلغاء' : 'Cancel'}
                      </button>
                    )}
                    <button
                      onClick={handleVtSave}
                      disabled={vtSaving}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl transition-all text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      <Save className="w-4 h-4" />
                      {vtSaving
                        ? (isRTL ? 'جاري الحفظ...' : 'Saving...')
                        : (isRTL ? 'حفظ' : 'Save')}
                    </button>
                  </div>
                </div>

                {/* Existing list */}
                <div>
                  <h3 className="font-bold text-gray-700 text-sm mb-3" dir={isRTL ? 'rtl' : 'ltr'}>
                    {isRTL ? 'الخطايا المضافة' : 'Existing Violations'}
                    <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                      {violationTypes.length}
                    </span>
                  </h3>

                  {vtLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin"></div>
                    </div>
                  ) : violationTypes.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      <ListChecks className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      {isRTL ? 'لا توجد أنواع خطية بعد' : 'No violation types yet'}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {violationTypes.map((vt) => (
                        <div
                          key={vt.id}
                          className={`flex items-center justify-between gap-3 p-4 rounded-xl border-2 transition-all ${
                            editingVt?.id === vt.id
                              ? 'border-amber-400 bg-amber-50'
                              : 'border-gray-100 bg-gray-50 hover:border-amber-200'
                          }`}
                        >
                          <div className="min-w-0 flex-1" dir={isRTL ? 'rtl' : 'ltr'}>
                            <p className="font-bold text-gray-800 text-sm truncate">{vt.name}</p>
                            <p className="text-amber-600 font-bold text-xs mt-0.5">
                              {vt.price.toFixed(2)} {isRTL ? 'ريال' : 'QAR'}
                            </p>
                          </div>
                          <div className="flex gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => handleVtStartEdit(vt)}
                              className="p-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all"
                              title={isRTL ? 'تعديل' : 'Edit'}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleVtDelete(vt)}
                              className="p-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all"
                              title={isRTL ? 'حذف' : 'Delete'}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 bg-gray-50 px-6 py-4 rounded-b-3xl border-t border-gray-200">
                <button
                  onClick={closeVtDialog}
                  className="w-full px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-bold"
                >
                  {isRTL ? 'إغلاق' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ─────────────────────────────────────────────────────────────────── */}

      </div>
    </div>
  );
};

export default CompaniesPage;
